import {
  AbridgedInvoiceMetadata,
  AbridgedInvoiceMetadataWithAmountDue,
  AccountGuid,
  BzDateFns,
  BzDateTime,
  bzExpect,
  CompanyGuid,
  Guid,
  getPaymentWorkflowOptionToPaymentMethod,
  IsoDateString,
  isNullish,
  LocalDateString,
  MaintenancePlanGuid,
  MaintenancePlanPaymentFlow,
  MaintenancePlanPaymentInterval,
  MaintenancePlanStatus,
  noOp,
  PaymentMethod,
  PaymentWorkflowOption,
  PricebookTaxRateGuid,
  usCentsToUsd,
  usdToUsCents,
} from '@breezy/shared'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import {
  faBuildingColumns,
  faClock,
  faCreditCard,
  faMoneyCheckDollarPen,
  faPaperPlane,
  faPiggyBank,
  faWallet,
} from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useCallback, useMemo, useState } from 'react'
import { useClient } from 'urql'
import RenderIf from '../../elements/RenderIf/RenderIf'
import Switch from '../../elements/Switch/Switch'
import { useFetchAbridgedInvoiceByMaintenancePlanGuid } from '../../hooks/fetch/useFetchAbridgedInvoiceByMaintenancePlanGuid'
import { trpc } from '../../hooks/trpc'
import { useUnsavedChangesWarning } from '../../hooks/useUnsavedChangesWarning'
import { INVOICES_DATA_QUERY, MAINTENANCE_PLAN_DETAILS_QUERY } from '../../pages/Invoices/Invoices.gql'
import { mapInvoiceToAbridgedInvoiceMetadata } from '../../pages/Invoices/invoiceUtils'
import { useTilledMerchantId } from '../../providers/CompanyFinancialConfigWrapper'
import { useExpectedCompanyTimeZoneId } from '../../providers/PrincipalUser'
import { OnsiteModal, OnsiteModalContent } from '../../ui/OnsiteModal/OnsiteModal'
import { CloseConfirmModal, useCloseConfirmModal } from '../../ui/OnsiteModal/useCloseConfirmModal'
import { useMessage } from '../../utils/antd-utils'
import { useModalState } from '../../utils/react-utils'
import OtherPaymentWorkflow from './components/OtherPaymentWorkflow/OtherPaymentWorkflow'
import PaymentWorkflowConfigForm, {
  PaymentWorkflowConfigSubmitFormData,
} from './components/PaymentWorkflowConfigForm/PaymentWorkflowConfigForm'
import TilledPaymentSubscriptionWorkflow from './components/TilledPaymentSubscriptionWorkflow/TilledPaymentSubscriptionWorkflow'
import TilledPaymentWorkflow from './components/TilledPaymentWorkflow/TilledPaymentWorkflow'
import { useSelfServePaymentWorkflow } from './hooks/useSelfServePaymentWorkflow'
import { usePaymentStatusModalContext } from './providers/PaymentStatusModalProvider'
import { usePaymentStatusContext } from './providers/PaymentStatusProvider'

export type PaymentWorkflowFormStyle = 'inline' | 'modal'

export type PaymentAmountUsc = number

export type MaintenancePlanActivationInput = {
  maintenancePlanGuid: MaintenancePlanGuid
  accountGuid: AccountGuid
  companyGuid: CompanyGuid
  businessUnitGuid?: Guid
  paymentInterval: MaintenancePlanPaymentInterval
  totalPriceUsc: number
  customBillingStartDate?: LocalDateString
  activationDate: LocalDateString
  taxRateGuid: PricebookTaxRateGuid | null
}

export type PaymentWorkflowMetadata = {
  /* If true, the amount in the payment workflow config step is editable */
  editableAmount?: boolean
} & (
  | {
      invoice: AbridgedInvoiceMetadata

      /* Required for SelfServe Payment workflow */
      invoiceAmountDueUsd?: number

      maintenancePlan?: never
    }
  | {
      invoice?: never
      invoiceAmountDueUsd?: never
      maintenancePlan: MaintenancePlanActivationInput
    }
)

export const defaultPaymentWorkflowFooterPaddingStyles = 'p-0 -mx-4 px-4 md:-mx-6 md:px-6 pt-4'

export const defaultPaymentWorkflowFooterStyles = 'gap-3 border-0 border-t-4 border-solid border-t-bz-gray-400'

export const PAYMENT_WORKFLOW_OPTIONS: {
  label: PaymentWorkflowOption
  method?: PaymentMethod
  icon: IconProp
}[] = [
  {
    label: 'Payment Link',
    icon: faPaperPlane,
  },
  {
    label: 'Credit Card',
    method: PaymentMethod.CARD,
    icon: faCreditCard,
  },
  {
    label: 'ACH',
    method: PaymentMethod.ACH,
    icon: faBuildingColumns,
  },
  {
    label: 'Check',
    method: PaymentMethod.CHECK,
    icon: faMoneyCheckDollarPen,
  },
  {
    label: 'Cash',
    method: PaymentMethod.CASH,
    icon: faWallet,
  },
  {
    label: 'Other',
    method: PaymentMethod.OTHER,
    icon: faPiggyBank,
  },
]

export type PaymentSuccessMethod = PaymentMethod | 'PAYMENT_LINK'

export type PaymentSuccessHandler = (paymentMethod: PaymentSuccessMethod, paymentAmountUsc: number) => void

type PaymentWorkflowContextType = {
  mode?: PaymentWorkflowMode
  setIsProcessingPayment: (isProcessingPayment: boolean) => void
  onPaymentSuccess?: PaymentSuccessHandler
  onPaymentError?: () => void
}

const PaymentWorkflowContext = React.createContext<PaymentWorkflowContextType>({
  mode: 'base',
  setIsProcessingPayment: () => {},
  onPaymentSuccess: () => {},
  onPaymentError: () => {},
})

export const usePaymentWorkflowContext = () => {
  return React.useContext(PaymentWorkflowContext)
}

type PaymentWorkflowOptionWithInfo = {
  option: PaymentWorkflowOption
  info?: string
  hide?: boolean
}

export type PaymentWorkflowOptionConfig = {
  /** Consumers can pass in a react component that will be rendered under the payment amount input  */
  paymentInfo?: React.ReactNode
  /* Allows consumer to specify which options they'd like to hide */
  hiddenOptions?: PaymentWorkflowOption[]
  /* Allows consumer to specify which options they'd like to disable */
  disabledOptions?: PaymentWorkflowOptionWithInfo[]
}

export type PaymentWorkflowMode = 'base' | 'embedded'
export type PaymentWorkflowWizardProps = {
  meta: PaymentWorkflowMetadata
  mode?: PaymentWorkflowMode
  onCancel?: () => void
  onBack?: () => void
  onPaymentSuccess?: PaymentSuccessHandler
  onPaymentError?: () => void
  setIsDirty?: (isDirty: boolean) => void
  hideCloseButton?: boolean
  onCollectLater?: () => void
} & PaymentWorkflowOptionConfig

const tilledDisabledOptions = ['Payment Link', 'ACH', 'Credit Card'] satisfies PaymentWorkflowOption[]

export const PaymentWorkflowWizard = React.memo<PaymentWorkflowWizardProps>(
  ({
    meta,
    paymentInfo,
    onCancel: externalOnCancel,
    onBack: externalOnBack,
    disabledOptions,
    hiddenOptions,
    mode = 'base',
    setIsDirty: externalSetIsDirty,
    onPaymentSuccess,
    onPaymentError,
    hideCloseButton = false,
    onCollectLater,
  }) => {
    const tilledMerchantId = useTilledMerchantId()
    const { isProcessingPayment } = usePaymentStatusContext()
    const { showPaymentStatusModal } = usePaymentStatusModalContext()

    const accountGuid = useMemo(
      () =>
        bzExpect(
          meta.invoice?.accountGuid ?? meta.maintenancePlan?.accountGuid,
          'accountGuid',
          'Account is required to collect payment',
        ),
      [meta],
    )

    const currentDisabledOptions = useMemo<PaymentWorkflowOptionWithInfo[]>(() => {
      const existingDisabledOptions = disabledOptions ?? []
      return tilledMerchantId
        ? existingDisabledOptions
        : [
            ...existingDisabledOptions,
            ...tilledDisabledOptions.map(option => ({
              option,
              info: 'Only available for Tilled Merchants',
            })),
          ].reduce<PaymentWorkflowOptionWithInfo[]>((acc, current) => {
            if (!acc.some(item => item.option === current.option)) {
              acc.push(current)
            }
            return acc
          }, [])
    }, [disabledOptions, tilledMerchantId])

    const [currentStep, setCurrentStep] = useState(0)

    const [paymentOption, setPaymentOption] = useState<PaymentWorkflowOption | undefined>(undefined)

    const setIsProcessingPayment = useCallback(
      (isProcessingPayment: boolean) => {
        if (isProcessingPayment) {
          showPaymentStatusModal({
            mode,
            type: 'payment-processing',
            data: {
              paymentMethod: getPaymentWorkflowOptionToPaymentMethod(paymentOption),
            },
          })
        }
      },
      [mode, paymentOption, showPaymentStatusModal],
    )

    const defaultPaymentAmountUsc = useMemo(() => {
      if (meta.invoiceAmountDueUsd) {
        return usdToUsCents(meta.invoiceAmountDueUsd)
      } else if (meta.maintenancePlan?.totalPriceUsc) {
        return meta.maintenancePlan.totalPriceUsc
      } else {
        return 0
      }
    }, [meta.invoiceAmountDueUsd, meta.maintenancePlan?.totalPriceUsc])
    const [paymentAmountUsc, setPaymentAmountUsc] = useState<number | undefined>(defaultPaymentAmountUsc)

    const [shouldSendReceipt, setShouldSendReceipt] = useState<boolean>(true)

    const canGoBack = useMemo(() => !!externalOnBack || currentStep > 0, [currentStep, externalOnBack])

    // biome-ignore lint/correctness/useExhaustiveDependencies: setCurrentStep is stable and not needed
    const moveForward = useCallback(() => setCurrentStep(currentStep => currentStep + 1), [setCurrentStep])
    // biome-ignore lint/correctness/useExhaustiveDependencies: setCurrentStep is stable and not needed
    const moveBack = useCallback(() => {
      if (externalOnBack && currentStep === 0) {
        externalOnBack()
      } else {
        setCurrentStep(currentStep => currentStep - 1)
      }
    }, [setCurrentStep, externalOnBack, currentStep])

    const [isInternallyDirty, setIsInternallyDirty] = useState(false)

    const setIsDirty = useCallback(
      (isDirty: boolean) => {
        setIsInternallyDirty(isDirty)
        externalSetIsDirty?.(isDirty)
      },
      [externalSetIsDirty],
    )

    const [withConfirmation, closeConfirmProps] = useCloseConfirmModal({
      isDirty: isInternallyDirty,
    })

    const openProcessingPaymentModal = useCallback(() => {
      showPaymentStatusModal({
        mode,
        type: 'payment-processing',
        data: {
          paymentMethod: getPaymentWorkflowOptionToPaymentMethod(paymentOption),
        },
      })
    }, [mode, paymentOption, showPaymentStatusModal])

    const onCancel = useCallback(
      (force?: boolean) => {
        // Just in case something is lying to TypeScript and sneakily passing an click event object or something
        if (force === true) {
          externalOnCancel?.()
          return
        }
        if (isProcessingPayment) {
          return
        }
        withConfirmation(externalOnCancel ?? (() => {}))
      },
      [isProcessingPayment, withConfirmation, externalOnCancel],
    )

    const {
      title: SelfServePaymentTitle,
      SelfServePaymentForm,
      onSelfServePaymentBack,
    } = useSelfServePaymentWorkflow({
      accountGuid,
      defaultPaymentAmountUsc,
      paymentAmountUsc,
      invoice: meta.invoice,
      onCancel,
      onBack: moveBack,
      setIsDirty: setIsDirty,
      onPaymentSuccess,
    })

    const onBack = useMemo(() => {
      if (!canGoBack) {
        return undefined
      }

      return () => {
        if (isProcessingPayment) {
          openProcessingPaymentModal()
          return
        }

        if (paymentOption === 'Payment Link') {
          onSelfServePaymentBack()
        } else {
          withConfirmation(moveBack)
        }
      }
    }, [
      canGoBack,
      isProcessingPayment,
      paymentOption,
      openProcessingPaymentModal,
      onSelfServePaymentBack,
      withConfirmation,
      moveBack,
    ])

    useUnsavedChangesWarning(isProcessingPayment)

    const onPaymentWorkflowConfigSubmit = useCallback(
      (data: PaymentWorkflowConfigSubmitFormData) => {
        setPaymentOption(data.paymentWorkflowOption)
        setPaymentAmountUsc(usdToUsCents(data.paymentAmountUsd))
        setShouldSendReceipt(data.shouldSendReceipt ?? true)
        moveForward()
      },
      [moveForward],
    )

    const title = useMemo(() => {
      switch (currentStep) {
        case 0:
          return 'Collect Payment'
        case 1: {
          const paymentOptionConfig = PAYMENT_WORKFLOW_OPTIONS.find(config => config.label === paymentOption)
          return paymentOption === 'Payment Link' ? SelfServePaymentTitle : paymentOptionConfig?.label
        }

        default:
          return undefined
      }
    }, [currentStep, paymentOption, SelfServePaymentTitle])

    const AchWorkflow = useMemo(() => {
      if (isNullish(paymentAmountUsc)) {
        return null
      }

      if (!isNullish(meta.invoice)) {
        return (
          <TilledPaymentWorkflow
            invoice={meta.invoice}
            paymentAmountUsc={paymentAmountUsc ?? 0}
            onCancel={onCancel}
            paymentMethod={PaymentMethod.ACH}
            setIsDirty={setIsDirty}
            shouldSendReceipt={shouldSendReceipt}
          />
        )
      }

      if (!isNullish(meta.maintenancePlan)) {
        return (
          <TilledPaymentSubscriptionWorkflow
            onCancel={externalOnCancel ? onCancel : undefined}
            maintenancePlan={meta.maintenancePlan}
            paymentMethod={PaymentMethod.ACH}
            setIsDirty={setIsDirty}
          />
        )
      }

      return null
    }, [
      externalOnCancel,
      meta.invoice,
      meta.maintenancePlan,
      onCancel,
      paymentAmountUsc,
      setIsDirty,
      shouldSendReceipt,
    ])

    const CreditCardWorkflow = useMemo(() => {
      if (isNullish(paymentAmountUsc)) {
        return null
      }

      if (!isNullish(meta.invoice)) {
        return (
          <TilledPaymentWorkflow
            invoice={meta.invoice}
            paymentAmountUsc={paymentAmountUsc ?? 0}
            onCancel={onCancel}
            paymentMethod={PaymentMethod.CARD}
            setIsDirty={setIsDirty}
            shouldSendReceipt={shouldSendReceipt}
          />
        )
      }

      if (!isNullish(meta.maintenancePlan)) {
        return (
          <TilledPaymentSubscriptionWorkflow
            onCancel={externalOnCancel ? onCancel : undefined}
            maintenancePlan={meta.maintenancePlan}
            paymentMethod={PaymentMethod.CARD}
            setIsDirty={setIsDirty}
          />
        )
      }

      return null
    }, [
      externalOnCancel,
      meta.invoice,
      meta.maintenancePlan,
      onCancel,
      paymentAmountUsc,
      setIsDirty,
      shouldSendReceipt,
    ])

    const content = useMemo(
      () => (
        <PaymentWorkflowContext.Provider
          value={{
            mode,
            setIsProcessingPayment,
            onPaymentSuccess,
            onPaymentError,
          }}
        >
          <OnsiteModalContent
            onClose={hideCloseButton && currentStep < 1 ? noOp : onCancel}
            onBack={onBack}
            disabled={isProcessingPayment || (hideCloseButton && currentStep < 1)}
            header={title}
          >
            <Switch value={currentStep}>
              {{
                0: () => (
                  <PaymentWorkflowConfigForm
                    paymentInfo={paymentInfo}
                    defaultPaymentAmountUsc={paymentAmountUsc ?? defaultPaymentAmountUsc}
                    amountUsd={usCentsToUsd(defaultPaymentAmountUsc ?? 0)}
                    disabledOptions={currentDisabledOptions}
                    hiddenOptions={hiddenOptions}
                    onSubmit={onPaymentWorkflowConfigSubmit}
                    editableAmount={meta.editableAmount}
                    paymentAmountLabel={meta.maintenancePlan ? 'Recurring payment amount' : undefined}
                    onCollectLater={meta.maintenancePlan ? onCollectLater : undefined}
                  />
                ),
                1: () => (
                  <Switch value={paymentOption}>
                    {{
                      'Payment Link': () => SelfServePaymentForm,
                      'Credit Card': () => CreditCardWorkflow,
                      ACH: () => AchWorkflow,
                      Check: () =>
                        paymentAmountUsc && meta.invoice ? (
                          <OtherPaymentWorkflow
                            paymentAmountUsc={paymentAmountUsc ?? 0}
                            invoice={meta.invoice}
                            onCancel={onCancel}
                            paymentMethod={PaymentMethod.CHECK}
                            setIsDirty={setIsDirty}
                            shouldSendReceipt={shouldSendReceipt}
                          />
                        ) : null,
                      Cash: () =>
                        paymentAmountUsc && meta.invoice ? (
                          <OtherPaymentWorkflow
                            paymentAmountUsc={paymentAmountUsc ?? 0}
                            invoice={meta.invoice}
                            onCancel={onCancel}
                            paymentMethod={PaymentMethod.CASH}
                            setIsDirty={setIsDirty}
                            shouldSendReceipt={shouldSendReceipt}
                          />
                        ) : null,
                      Other: () =>
                        paymentAmountUsc && meta.invoice ? (
                          <OtherPaymentWorkflow
                            paymentAmountUsc={paymentAmountUsc ?? 0}
                            invoice={meta.invoice}
                            onCancel={onCancel}
                            paymentMethod={PaymentMethod.OTHER}
                            setIsDirty={setIsDirty}
                            shouldSendReceipt={shouldSendReceipt}
                          />
                        ) : null,
                      default: () => null,
                    }}
                  </Switch>
                ),
                default: () => null,
              }}
            </Switch>
            <RenderIf if={!!onCollectLater}>
              <div className="mt-12 flex items-center justify-center">
                <button
                  type="button"
                  onClick={onCollectLater}
                  className="hover:text-bz-link-hover m-0 flex cursor-pointer appearance-none items-center gap-2 border-none bg-transparent p-0 text-[16px] leading-[24px] text-bz-link"
                  data-dd-action-name="BZ Payment Workflow - Collect Payment Later"
                >
                  <FontAwesomeIcon icon={faClock} />
                  <span>Collect Payment Later</span>
                </button>
              </div>
            </RenderIf>
          </OnsiteModalContent>
        </PaymentWorkflowContext.Provider>
      ),
      [
        mode,
        setIsProcessingPayment,
        onPaymentSuccess,
        onPaymentError,
        hideCloseButton,
        currentStep,
        onCancel,
        onBack,
        isProcessingPayment,
        title,
        paymentInfo,
        paymentAmountUsc,
        defaultPaymentAmountUsc,
        currentDisabledOptions,
        hiddenOptions,
        onPaymentWorkflowConfigSubmit,
        meta.editableAmount,
        meta.maintenancePlan,
        meta.invoice,
        paymentOption,
        SelfServePaymentForm,
        CreditCardWorkflow,
        AchWorkflow,
        setIsDirty,
        onCollectLater,
        shouldSendReceipt,
      ],
    )

    if (mode === 'embedded') {
      return (
        <>
          {content}
          <CloseConfirmModal {...closeConfirmProps} />
        </>
      )
    }

    return (
      <>
        <OnsiteModal onClose={onCancel} size="large">
          {content}
        </OnsiteModal>
        <CloseConfirmModal {...closeConfirmProps} />
      </>
    )
  },
)

PaymentWorkflowWizard.displayName = 'PaymentWorkflowWizard'

export const usePaymentWorkflowProps = (
  maintenancePlanGuid?: MaintenancePlanGuid,
  paymentFlow?: MaintenancePlanPaymentFlow,
  refetch?: () => void,
) => {
  const tzId = useExpectedCompanyTimeZoneId()
  const message = useMessage()
  const trpcUtils = trpc.useUtils()

  const [activatedMaintenancePlanMeta, setActivatedMaintenancePlanMeta] = useState<
    { paymentMethod: PaymentSuccessMethod; paymentAmountUsc: number } | undefined
  >()
  const [scheduledMaintenancePlan, setScheduledMaintenancePlan] = useState<
    | {
        maintenancePlanGuid: MaintenancePlanGuid
        scheduledActivationAt: IsoDateString
      }
    | undefined
  >(undefined)

  const oneShotInvoiceLoader = useFetchAbridgedInvoiceByMaintenancePlanGuid({
    maintenancePlanGuid: maintenancePlanGuid ?? '',
    pause: !maintenancePlanGuid,
    status: 'OPEN',
  })

  // BZ-450: when there is no OPEN invoice to collect on, distinguish "already paid" (benign) from a
  // genuinely missing invoice so we never show the alarming "Contact Breezy Support" error for a plan
  // whose invoice is simply paid.
  const paidInvoiceLoader = useFetchAbridgedInvoiceByMaintenancePlanGuid({
    maintenancePlanGuid: maintenancePlanGuid ?? '',
    // Only needed to disambiguate "already paid" vs "missing" when there is no OPEN invoice — pause it
    // otherwise to avoid an extra subscription on the common (OPEN-invoice / already-active) path.
    pause: !maintenancePlanGuid || !!oneShotInvoiceLoader.invoice,
    status: 'PAID',
  })

  const [paymentWorkflowOpen, showPaymentWorkflow, hidePaymentWorkflow] = useModalState(false)
  const [paymentSubscriptionWorkflowOpen, showPaymentSubscriptionWorkflow, hidePaymentSubscriptionWorkflow] =
    useModalState(false)

  const [activatedModalOpen, showActivatedModal, hideActivatedModal] = useModalState(false)

  const { hidePaymentStatusModal } = usePaymentStatusModalContext()
  const resolveOneShotPaymentOutcome = useCallback(
    async (paymentMethod: PaymentSuccessMethod, paymentAmountUsc: number) => {
      if (!maintenancePlanGuid) {
        return
      }

      try {
        const temporalStatus = await trpcUtils.client.maintenancePlans['maintenance-plans:get-temporal-status'].query({
          maintenancePlanGuid,
        })

        hidePaymentStatusModal()
        hidePaymentWorkflow()
        refetch?.()

        if (temporalStatus.status === MaintenancePlanStatus.SCHEDULED && temporalStatus.scheduledActivationAt) {
          setScheduledMaintenancePlan({
            maintenancePlanGuid,
            scheduledActivationAt: temporalStatus.scheduledActivationAt,
          })
          return
        }

        showActivatedModal()
        setActivatedMaintenancePlanMeta({ paymentMethod, paymentAmountUsc })
      } catch {
        hidePaymentStatusModal()
        hidePaymentWorkflow()
        refetch?.()
        showActivatedModal()
        setActivatedMaintenancePlanMeta({ paymentMethod, paymentAmountUsc })
      }
    },
    [hidePaymentStatusModal, hidePaymentWorkflow, maintenancePlanGuid, refetch, showActivatedModal, trpcUtils.client],
  )

  const onPaymentSuccess = useCallback<PaymentSuccessHandler>(
    (paymentMethod, paymentAmountUsc) => {
      void resolveOneShotPaymentOutcome(paymentMethod, paymentAmountUsc)
    },
    [resolveOneShotPaymentOutcome],
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: setActivatedMaintenancePlanMeta is stable and not needed
  const onPaymentSubscriptionSuccess = useCallback<PaymentSuccessHandler>(
    (paymentMethod, paymentAmountUsc) => {
      showActivatedModal()
      hidePaymentStatusModal()
      hidePaymentSubscriptionWorkflow()
      setActivatedMaintenancePlanMeta({ paymentMethod, paymentAmountUsc })
      refetch?.()
    },
    [
      hidePaymentStatusModal,
      hidePaymentSubscriptionWorkflow,
      setActivatedMaintenancePlanMeta,
      refetch,
      showActivatedModal,
    ],
  )

  const handleCollectPayment = useCallback(() => {
    if (paymentFlow === MaintenancePlanPaymentFlow.NONE) {
      // This means the plan was configured to auto-renew, but the user
      // has not yet paid.
      showPaymentSubscriptionWorkflow()
    } else {
      if (!oneShotInvoiceLoader.invoice) {
        // Don't misreport while either invoice subscription is still loading — a not-yet-loaded OPEN
        // invoice would otherwise be transiently shown as "already paid".
        if (oneShotInvoiceLoader.loading || paidInvoiceLoader.loading) {
          return
        }
        if (paidInvoiceLoader.invoice) {
          message.info("This plan's invoice is already paid.")
        } else {
          message.error('Missing invoice. Contact Breezy Support.')
        }
      } else {
        showPaymentWorkflow()
      }
    }
  }, [
    paymentFlow,
    showPaymentSubscriptionWorkflow,
    showPaymentWorkflow,
    message,
    oneShotInvoiceLoader.invoice,
    oneShotInvoiceLoader.loading,
    paidInvoiceLoader.invoice,
    paidInvoiceLoader.loading,
  ])

  const paymentWorkflowWizardProps = useMemo(() => {
    const invoice = oneShotInvoiceLoader.invoice
    if (!invoice || !paymentWorkflowOpen) {
      return undefined
    }
    return {
      meta: {
        invoice,
        invoiceAmountDueUsd: usCentsToUsd(invoice.amountDueUsc),
        editableAmount: false,
      },
      onPaymentSuccess,
      onCancel: hidePaymentWorkflow,
      mode: 'base' as const,
    }
  }, [hidePaymentWorkflow, oneShotInvoiceLoader.invoice, onPaymentSuccess, paymentWorkflowOpen])

  const getPaymentSubscriptionWizardProps = useCallback(
    (mp?: {
      maintenancePlanGuid: MaintenancePlanGuid
      accountGuid: AccountGuid
      companyGuid: CompanyGuid
      paymentInterval?: MaintenancePlanPaymentInterval
      paymentAmountUsc: number
      pricebookTaxRateGuid?: PricebookTaxRateGuid
      activatedAt?: IsoDateString
    }) => {
      if (!mp || !paymentSubscriptionWorkflowOpen) return undefined

      return {
        meta: {
          editableAmount: false,
          maintenancePlan: {
            maintenancePlanGuid: mp.maintenancePlanGuid,
            accountGuid: mp.accountGuid,
            companyGuid: mp.companyGuid,
            paymentInterval: mp.paymentInterval ?? MaintenancePlanPaymentInterval.YEARLY,
            totalPriceUsc: mp.paymentAmountUsc,
            taxRateGuid: mp.pricebookTaxRateGuid ?? null,
            activationDate: mp.activatedAt
              ? BzDateFns.formatLocalDate(BzDateFns.parseISO(mp.activatedAt, tzId))
              : BzDateTime.now(tzId).toLocalDateString(),
          },
        },
        onPaymentSuccess: onPaymentSubscriptionSuccess,
        hiddenOptions: ['Payment Link'] as PaymentWorkflowOption[],
        onCancel: hidePaymentSubscriptionWorkflow,
        disabledOptions: [
          {
            option: 'Cash',
            info: 'Cannot be used for auto-renewing plans',
          },
          {
            option: 'Check',
            info: 'Cannot be used for auto-renewing plans',
          },
          {
            option: 'Other',
            info: 'Cannot be used for auto-renewing plans',
          },
        ] as PaymentWorkflowOptionWithInfo[],
      }
    },
    [hidePaymentSubscriptionWorkflow, onPaymentSubscriptionSuccess, tzId, paymentSubscriptionWorkflowOpen],
  )

  const getMaintenancePlanActivatedProps = useCallback(
    ({
      primaryContactEmail,
      locationGuid,
      mpForPaymentWizard,
    }: {
      primaryContactEmail?: string
      locationGuid?: Guid
      mpForPaymentWizard?: {
        maintenancePlanGuid: MaintenancePlanGuid
        accountGuid: AccountGuid
        companyGuid: CompanyGuid
        paymentInterval?: MaintenancePlanPaymentInterval
        paymentAmountUsc: number
      }
    }) => {
      if (!activatedModalOpen || !mpForPaymentWizard || !locationGuid || !primaryContactEmail) return undefined

      return {
        primaryContactEmail,
        maintenancePlanGuid: mpForPaymentWizard.maintenancePlanGuid,
        data: {
          isFreePlan: false,
          isAutoRenewing: true,
          isImportedPlan: false,
          billingPaymentInterval: mpForPaymentWizard.paymentInterval ?? MaintenancePlanPaymentInterval.MONTHLY,
          billingAnchorDay: BzDateTime.now(tzId).toJsJodaLocalDate().dayOfMonth().toString(),
          activationDate: BzDateTime.now(tzId).toLocalDateString(),
          accountGuid: mpForPaymentWizard.accountGuid,
          locationGuid,
          numFixedDurationYears: undefined,
        },
        onClose: hideActivatedModal,
        paymentMethod: activatedMaintenancePlanMeta?.paymentMethod,
        paymentAmountUsc: activatedMaintenancePlanMeta?.paymentAmountUsc,
        hideViewPlanDetails: true,
        closeText: 'Close',
      }
    },
    [hideActivatedModal, activatedMaintenancePlanMeta, activatedModalOpen, tzId],
  )

  const getMaintenancePlanScheduledProps = useCallback(() => {
    if (!scheduledMaintenancePlan) {
      return undefined
    }

    return {
      maintenancePlanGuid: scheduledMaintenancePlan.maintenancePlanGuid,
      scheduledActivationAt: scheduledMaintenancePlan.scheduledActivationAt,
      onClose: () => {
        setScheduledMaintenancePlan(undefined)
      },
    }
  }, [scheduledMaintenancePlan])

  return {
    showPaymentWorkflow,
    showPaymentSubscriptionWorkflow,
    handleCollectPayment,
    paymentWorkflowWizardProps,
    getPaymentSubscriptionWizardProps,
    getMaintenancePlanActivatedProps,
    getMaintenancePlanScheduledProps,
  }
}

type PaymentWorkflowMaintenancePlan = {
  maintenancePlanGuid: MaintenancePlanGuid
  paymentFlow: MaintenancePlanPaymentFlow
  accountGuid: AccountGuid
  companyGuid: CompanyGuid
  paymentInterval?: MaintenancePlanPaymentInterval
  paymentAmountUsc: number
  pricebookTaxRateGuid?: PricebookTaxRateGuid
  activatedAt?: IsoDateString
  primaryContactEmail?: string
  locationGuid: Guid
  planDefinition?: { marketingInfo?: { name?: string } }
}

export const useAsyncPaymentWorkflowProps = (refetch: () => void) => {
  const client = useClient()
  const tzId = useExpectedCompanyTimeZoneId()
  const message = useMessage()
  const trpcUtils = trpc.useUtils()
  const [activatedMaintenancePlanMeta, setActivatedMaintenancePlanMeta] = useState<
    { paymentMethod: PaymentSuccessMethod; paymentAmountUsc: number } | undefined
  >()

  const [oneShotInvoice, setOneShotInvoice] = useState<AbridgedInvoiceMetadataWithAmountDue | undefined>(undefined)

  const [paymentWorkflowForPlan, privateSetPaymentWorkflowForPlan] = useState<
    PaymentWorkflowMaintenancePlan | undefined
  >(undefined)
  const [paymentSubscriptionWorkflowForPlan, setPaymentSubscriptionWorkflowForPlan] = useState<
    PaymentWorkflowMaintenancePlan | undefined
  >(undefined)
  const [activatedModalForPlan, setActivatedModalForPlan] = useState<PaymentWorkflowMaintenancePlan | undefined>(
    undefined,
  )
  const [scheduledModalForPlan, setScheduledModalForPlan] = useState<
    | {
        maintenancePlanGuid: MaintenancePlanGuid
        scheduledActivationAt: IsoDateString
      }
    | undefined
  >(undefined)

  // biome-ignore lint/correctness/useExhaustiveDependencies: privateSetPaymentWorkflowForPlan is stable and not needed
  const setPaymentWorkflowForPlan = useCallback(
    async (mp: PaymentWorkflowMaintenancePlan | undefined) => {
      if (isNullish(mp)) {
        privateSetPaymentWorkflowForPlan(undefined)
        setOneShotInvoice(undefined)
        return
      }
      const res = await client.query(INVOICES_DATA_QUERY, {
        where: {
          maintenancePlanLink: {
            maintenancePlanGuid: {
              _eq: mp.maintenancePlanGuid,
            },
          },
          status: {
            _eq: 'OPEN',
          },
        },
      })
      const mpRes = await client.query(MAINTENANCE_PLAN_DETAILS_QUERY, {
        maintenancePlanGuid: mp.maintenancePlanGuid,
      })
      const maintenancePlan = mpRes.data?.maintenancePlansQueryableByPk
      if (!maintenancePlan) {
        message.error('Missing maintenance plan. Contact Breezy Support.')
        return
      }
      const rawInvoice = res.data && (res.data.invoices?.length ?? 0) > 0 ? res.data.invoices[0] : undefined

      const invoice = rawInvoice ? mapInvoiceToAbridgedInvoiceMetadata(rawInvoice.invoiceGuid, rawInvoice) : undefined
      if (!invoice) {
        // BZ-450: no OPEN invoice to collect on. Show a benign message if the plan's invoice is simply
        // already paid, rather than the alarming "Contact Breezy Support" error.
        const paidRes = await client.query(INVOICES_DATA_QUERY, {
          where: {
            maintenancePlanLink: {
              maintenancePlanGuid: {
                _eq: mp.maintenancePlanGuid,
              },
            },
            status: {
              _eq: 'PAID',
            },
          },
        })
        if ((paidRes.data?.invoices?.length ?? 0) > 0) {
          message.info("This plan's invoice is already paid.")
        } else {
          message.error('Missing invoice. Contact Breezy Support.')
        }
      } else {
        setOneShotInvoice(invoice)
        privateSetPaymentWorkflowForPlan({
          ...mp,
          planDefinition: maintenancePlan.maintenancePlanDefinition,
        })
      }
    },
    [client, message, privateSetPaymentWorkflowForPlan],
  )

  const { hidePaymentStatusModal } = usePaymentStatusModalContext()
  const resolveAsyncOneShotPaymentOutcome = useCallback(
    async (paymentMethod: PaymentSuccessMethod, paymentAmountUsc: number) => {
      if (!paymentWorkflowForPlan) {
        return
      }

      try {
        const temporalStatus = await trpcUtils.client.maintenancePlans['maintenance-plans:get-temporal-status'].query({
          maintenancePlanGuid: paymentWorkflowForPlan.maintenancePlanGuid,
        })

        hidePaymentStatusModal()
        setPaymentWorkflowForPlan(undefined)
        refetch()

        if (temporalStatus.status === MaintenancePlanStatus.SCHEDULED && temporalStatus.scheduledActivationAt) {
          setScheduledModalForPlan({
            maintenancePlanGuid: paymentWorkflowForPlan.maintenancePlanGuid,
            scheduledActivationAt: temporalStatus.scheduledActivationAt,
          })
          return
        }

        if (paymentWorkflowForPlan.planDefinition?.marketingInfo) {
          setActivatedModalForPlan(paymentWorkflowForPlan)
        }
        setActivatedMaintenancePlanMeta({ paymentMethod, paymentAmountUsc })
      } catch {
        hidePaymentStatusModal()
        setPaymentWorkflowForPlan(undefined)
        refetch()
        if (paymentWorkflowForPlan.planDefinition?.marketingInfo) {
          setActivatedModalForPlan(paymentWorkflowForPlan)
        }
        setActivatedMaintenancePlanMeta({ paymentMethod, paymentAmountUsc })
      }
    },
    [hidePaymentStatusModal, paymentWorkflowForPlan, refetch, setPaymentWorkflowForPlan, trpcUtils.client],
  )

  const onPaymentSuccess = useCallback<PaymentSuccessHandler>(
    (paymentMethod, paymentAmountUsc) => {
      void resolveAsyncOneShotPaymentOutcome(paymentMethod, paymentAmountUsc)
    },
    [resolveAsyncOneShotPaymentOutcome],
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: setActivatedMaintenancePlanMeta is stable and not needed
  const onPaymentSubscriptionSuccess = useCallback<PaymentSuccessHandler>(
    (paymentMethod, paymentAmountUsc) => {
      if (paymentSubscriptionWorkflowForPlan?.planDefinition?.marketingInfo) {
        setActivatedModalForPlan(paymentSubscriptionWorkflowForPlan)
      }
      hidePaymentStatusModal()
      setPaymentSubscriptionWorkflowForPlan(undefined)
      setActivatedMaintenancePlanMeta({ paymentMethod, paymentAmountUsc })
      refetch()
    },
    [hidePaymentStatusModal, refetch, setActivatedMaintenancePlanMeta, paymentSubscriptionWorkflowForPlan],
  )

  const handleCollectPayment = useCallback(
    async (mp: PaymentWorkflowMaintenancePlan) => {
      if (mp.paymentFlow === MaintenancePlanPaymentFlow.NONE) {
        // This means the plan was configured to auto-renew, but the user
        // has not yet paid.
        setPaymentSubscriptionWorkflowForPlan(mp)
      } else {
        setPaymentWorkflowForPlan(mp)
      }
    },
    [setPaymentWorkflowForPlan],
  )

  const paymentWorkflowWizardProps = useMemo(() => {
    if (!oneShotInvoice || !paymentWorkflowForPlan) {
      return undefined
    }
    return {
      meta: {
        invoice: oneShotInvoice,
        invoiceAmountDueUsd: usCentsToUsd(oneShotInvoice.amountDueUsc),
        editableAmount: false,
      },
      onPaymentSuccess,
      onCancel: () => {
        setPaymentWorkflowForPlan(undefined)
      },
      mode: 'base' as const,
    }
  }, [onPaymentSuccess, paymentWorkflowForPlan, oneShotInvoice, setPaymentWorkflowForPlan])

  const paymentSubscriptionWizardProps = useMemo(() => {
    if (!paymentSubscriptionWorkflowForPlan) return undefined

    return {
      meta: {
        editableAmount: false,
        maintenancePlan: {
          maintenancePlanGuid: paymentSubscriptionWorkflowForPlan.maintenancePlanGuid,
          accountGuid: paymentSubscriptionWorkflowForPlan.accountGuid,
          companyGuid: paymentSubscriptionWorkflowForPlan.companyGuid,
          paymentInterval: paymentSubscriptionWorkflowForPlan.paymentInterval ?? MaintenancePlanPaymentInterval.YEARLY,
          totalPriceUsc: paymentSubscriptionWorkflowForPlan.paymentAmountUsc,
          taxRateGuid: paymentSubscriptionWorkflowForPlan.pricebookTaxRateGuid ?? null,
          activationDate: paymentSubscriptionWorkflowForPlan.activatedAt
            ? BzDateFns.formatLocalDate(BzDateFns.parseISO(paymentSubscriptionWorkflowForPlan.activatedAt, tzId))
            : BzDateTime.now(tzId).toLocalDateString(),
        },
      },
      onPaymentSuccess: onPaymentSubscriptionSuccess,
      hiddenOptions: ['Payment Link'] as PaymentWorkflowOption[],
      onCancel: () => {
        setPaymentSubscriptionWorkflowForPlan(undefined)
      },
      disabledOptions: [
        {
          option: 'Cash',
          info: 'Cannot be used for auto-renewing plans',
        },
        {
          option: 'Check',
          info: 'Cannot be used for auto-renewing plans',
        },
        {
          option: 'Other',
          info: 'Cannot be used for auto-renewing plans',
        },
      ] as PaymentWorkflowOptionWithInfo[],
    }
  }, [onPaymentSubscriptionSuccess, tzId, paymentSubscriptionWorkflowForPlan])

  const maintenancePlanActivatedProps = useMemo(() => {
    if (isNullish(activatedModalForPlan)) return undefined

    return {
      primaryContactEmail: activatedModalForPlan.primaryContactEmail,
      maintenancePlanGuid: activatedModalForPlan.maintenancePlanGuid,
      data: {
        isFreePlan: false,
        isAutoRenewing: true,
        isImportedPlan: false,
        billingPaymentInterval: activatedModalForPlan.paymentInterval ?? MaintenancePlanPaymentInterval.MONTHLY,
        billingAnchorDay: BzDateTime.now(tzId).toJsJodaLocalDate().dayOfMonth().toString(),
        activationDate: BzDateTime.now(tzId).toLocalDateString(),
        accountGuid: activatedModalForPlan.accountGuid,
        locationGuid: activatedModalForPlan.locationGuid,
        numFixedDurationYears: undefined,
      },
      onClose: () => {
        setActivatedModalForPlan(undefined)
      },
      paymentMethod: activatedMaintenancePlanMeta?.paymentMethod,
      paymentAmountUsc: activatedMaintenancePlanMeta?.paymentAmountUsc,
      hideViewPlanDetails: true,
      closeText: 'Close',
    }
  }, [activatedMaintenancePlanMeta, activatedModalForPlan, tzId])

  const maintenancePlanScheduledProps = useMemo(() => {
    if (isNullish(scheduledModalForPlan)) {
      return undefined
    }

    return {
      maintenancePlanGuid: scheduledModalForPlan.maintenancePlanGuid,
      scheduledActivationAt: scheduledModalForPlan.scheduledActivationAt,
      onClose: () => {
        setScheduledModalForPlan(undefined)
      },
    }
  }, [scheduledModalForPlan])

  return {
    setPaymentWorkflowForPlan,
    paymentWorkflowWizardProps,
    paymentSubscriptionWizardProps,
    maintenancePlanActivatedProps,
    maintenancePlanScheduledProps,
    handleCollectPayment,
  }
}

export default PaymentWorkflowWizard
